import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { date as fmtDate } from "@/lib/format";
import { Button, Modal, PageHeader, Pill, Table, Td, Th, Input } from "@/components/ui";
import { confirmPopup } from "@/components/popup";

interface PromoCode {
  id: number;
  code: string;
  discount_percent: number;
  max_uses: number | null;
  used_count: number;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

interface PromoCodeListResponse {
  items: PromoCode[];
  total: number;
}

interface UsageItem {
  id: number;
  user_id: number;
  used_at: string;
  original_amount: number;
  discount_amount: number;
}

interface CreateForm {
  code: string;
  discount_percent: string;
  max_uses: string;
  expires_at: string;
}

const EMPTY_FORM: CreateForm = {
  code: "",
  discount_percent: "10",
  max_uses: "",
  expires_at: "",
};

const rub = new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB" });
const dt = (s: string | null) => (s ? fmtDate(s) : "—");

export default function PromoCodes() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [usagesPromo, setUsagesPromo] = useState<PromoCode | null>(null);
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);

  const q = useQuery<PromoCodeListResponse>({
    queryKey: ["promo-codes"],
    queryFn: () => api("/api/admin/promo-codes"),
  });

  const usagesQ = useQuery<{ items: UsageItem[] }>({
    queryKey: ["promo-usages", usagesPromo?.id],
    queryFn: () => api(`/api/admin/promo-codes/${usagesPromo!.id}/usages`),
    enabled: !!usagesPromo,
  });

  const create = useMutation({
    mutationFn: () =>
      api("/api/admin/promo-codes", {
        method: "POST",
        body: JSON.stringify({
          code: form.code.trim().toUpperCase(),
          discount_percent: Number(form.discount_percent),
          max_uses: form.max_uses ? Number(form.max_uses) : null,
          expires_at: form.expires_at || null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["promo-codes"] });
      setCreateOpen(false);
      setForm(EMPTY_FORM);
    },
  });

  const deactivate = useMutation({
    mutationFn: (id: number) =>
      api(`/api/admin/promo-codes/${id}/deactivate`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["promo-codes"] }),
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api(`/api/admin/promo-codes/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["promo-codes"] }),
  });

  const items = q.data?.items ?? [];

  const upd = <K extends keyof CreateForm>(k: K, v: CreateForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Промокоды"
        right={
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            <div className="text-zinc-400 text-sm">всего: {q.data?.total ?? 0}</div>
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              + Создать
            </Button>
          </div>
        }
      />

      {q.isLoading && <div className="text-zinc-400">Загрузка…</div>}
      {q.isError && <div className="text-red-400">Ошибка загрузки</div>}

      {items.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>код</Th>
              <Th>скидка</Th>
              <Th>использовано</Th>
              <Th>истекает</Th>
              <Th>статус</Th>
              <Th fit>{" "}</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr
                key={p.id}
                className="hover:bg-zinc-900/50 cursor-pointer"
                onClick={() => setUsagesPromo(p)}
              >
                <Td className="font-mono text-zinc-100">{p.code}</Td>
                <Td className="text-emerald-400">{p.discount_percent}%</Td>
                <Td className="text-zinc-300">
                  {p.used_count}
                  {p.max_uses != null ? `/${p.max_uses}` : ""}
                </Td>
                <Td>{dt(p.expires_at)}</Td>
                <Td>
                  {p.is_active ? (
                    <Pill tone="success">активен</Pill>
                  ) : (
                    <Pill tone="danger">неактивен</Pill>
                  )}
                </Td>
                <Td className="flex gap-2">
                  {p.is_active && (
                    <Button
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        deactivate.mutate(p.id);
                      }}
                    >
                      Деактивировать
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (await confirmPopup({ title: "Удалить промокод?", body: p.code, danger: true, confirmLabel: "Удалить" })) remove.mutate(p.id);
                    }}
                  >
                    Удалить
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Create modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Создать промокод">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="код">
              <Input
                value={form.code}
                placeholder="SUMMER20"
                onChange={(e) => upd("code", e.target.value)}
                autoFocus
              />
            </Field>
            <Field label="скидка %">
              <Input
                value={form.discount_percent}
                type="number"
                min={1}
                max={100}
                onChange={(e) => upd("discount_percent", e.target.value)}
              />
            </Field>
            <Field label="макс. использований (пусто = без лимита)">
              <Input
                value={form.max_uses}
                type="number"
                min={1}
                placeholder="без лимита"
                onChange={(e) => upd("max_uses", e.target.value)}
              />
            </Field>
            <Field label="истекает (пусто = бессрочно)">
              <Input
                value={form.expires_at}
                type="datetime-local"
                onChange={(e) => upd("expires_at", e.target.value)}
              />
            </Field>
          </div>
          {create.isError && (
            <div className="text-xs text-red-400">{(create.error as Error).message}</div>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              disabled={!form.code.trim() || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? "Создаём…" : "Создать"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Usages modal */}
      <Modal
        open={!!usagesPromo}
        onClose={() => setUsagesPromo(null)}
        title={`Использования: ${usagesPromo?.code}`}
      >
        {usagesQ.isLoading && <div className="text-zinc-400">Загрузка…</div>}
        {usagesQ.data && usagesQ.data.items.length === 0 && (
          <div className="text-zinc-500 text-sm">Пока никто не использовал</div>
        )}
        {usagesQ.data && usagesQ.data.items.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>user_id</Th>
                <Th>дата</Th>
                <Th>сумма</Th>
                <Th>скидка</Th>
              </tr>
            </thead>
            <tbody>
              {usagesQ.data.items.map((u) => (
                <tr key={u.id}>
                  <Td className="text-zinc-300">{u.user_id}</Td>
                  <Td className="text-zinc-400 text-xs">
                    {new Date(u.used_at).toLocaleString("ru-RU")}
                  </Td>
                  <Td>{rub.format(u.original_amount)}</Td>
                  <Td className="text-emerald-400">-{rub.format(u.discount_amount)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
